import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { DataTableProvider } from '../../DataTable/DataTableProvider';
import { DataTableContent } from '../../DataTable/DataTableContent';
import { InboxIcon } from '../../icons';
import { createOrderSourceColumns } from '../columns';
import type { OrderSourcesTableProps } from '../types';

function EmptyOrderSourcesState({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-16">
      <InboxIcon className="mb-4 size-11 text-grey" />
      <p className="font-inter text-sm text-grey">{title}</p>
    </div>
  );
}

export function OrderSourcesTable({
  orderSources,
  isLoading = false,
  isFetching = false,
  onRowClick,
  onStatusToggle,
  sorting,
  onSortingChange,
  className,
  canWrite = false,
}: OrderSourcesTableProps) {
  const navigate = useNavigate({ from: '/order_sources' });
  const handleEdit = useCallback(
    (id: number) => {
      navigate({
        to: '/order_sources/$id/edit',
        params: { id: String(id) },
      });
    },
    [navigate],
  );
  const columns = useMemo(
    () => createOrderSourceColumns({ onStatusToggle, onEdit: handleEdit, showRowActions: canWrite }),
    [onStatusToggle, handleEdit, canWrite],
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <DataTableProvider
        columns={columns}
        data={orderSources}
        isLoading={isLoading}
        isFetching={isFetching}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={onSortingChange}
      >
        <DataTableContent
          onRowClick={onRowClick}
          emptyState={<EmptyOrderSourcesState title="No order sources found" />}
        />
      </DataTableProvider>
    </div>
  );
}
